import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLive } from "@/context/LiveContext";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  Factory, LayoutDashboard, ClipboardList, CheckSquare, Users, LogOut, Sun, Moon, Menu, X,
} from "lucide-react";

function LiveBadge() {
  const { connected } = useLive();
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card" data-testid="live-badge">
      <span className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-400 led-pulse" : "bg-slate-500"}`} />
      <span className="text-xs font-mono-tag uppercase tracking-wider text-muted-foreground">
        {connected ? "Live Sync" : "Offline"}
      </span>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
  };

  const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["owner", "manager", "captain", "client"] },
    { to: "/jobs", label: "Jobs", icon: ClipboardList, roles: ["owner", "manager", "captain", "client"] },
    { to: "/approvals", label: "Approvals", icon: CheckSquare, roles: ["owner", "manager"] },
    { to: "/users", label: "Users", icon: Users, roles: ["owner", "manager"] },
  ].filter((n) => n.roles.includes(user.role));

  const SidebarInner = () => (
    <>
      <div className="flex items-center gap-3 px-5 h-16 border-b border-border">
        <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
          <Factory className="h-5 w-5 text-primary-foreground" />
        </div>
        <div className="min-w-0">
          <div className="font-heading font-bold leading-none truncate">ENRICHER</div>
          <div className="label-eyebrow truncate">{user.company_name}</div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`
            }
          >
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="px-3 py-2">
          <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user.name}</div>
          <div className="label-eyebrow text-primary">{ROLE_LABELS[user.role]}</div>
        </div>
        <Button variant="ghost" className="w-full justify-start text-muted-foreground" onClick={logout} data-testid="logout-button">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card/40 fixed inset-y-0">
        <SidebarInner />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 flex flex-col border-r border-border bg-card">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 border-b border-border flex items-center justify-between px-4 sm:px-6 sticky top-0 bg-background/80 backdrop-blur-md z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <h2 className="font-heading font-bold text-lg hidden sm:block">Operations Console</h2>
          </div>
          <div className="flex items-center gap-3">
            <LiveBadge />
            <Button variant="outline" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}
